import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './WeaponsSection.css';

gsap.registerPlugin(ScrollTrigger);

const weapons = [
  {
    id: 1,
    name: 'REBELLION',
    type: 'Devil Arm',
    description: 'Dante\'s signature sword, a memento from his father Sparda. This iconic blade can be wielded with devastating combos and transforms with Dante\'s demonic power.',
    image: '/images/weapons/rebellion.png',
    stats: { power: 85, speed: 75, range: 70 }
  },
  {
    id: 2,
    name: 'EBONY & IVORY',
    type: 'Firearms',
    description: 'Twin semi-automatic pistols custom-made for Dante. Ebony is black with a left-hand grip, Ivory is white with a right-hand grip. Never-ending style.',
    image: '/images/weapons/ebony-ivory.png',
    stats: { power: 50, speed: 95, range: 90 }
  },
  {
    id: 3,
    name: 'CERBERUS',
    type: 'Devil Arm',
    description: 'A three-section nunchaku infused with ice elemental power. Obtained from the demonic guardian of the Temen-ni-gru. Freezes enemies on impact.',
    image: '/images/weapons/cerberus.png',
    stats: { power: 70, speed: 90, range: 60 }
  },
  {
    id: 4,
    name: 'SHOTGUN',
    type: 'Firearm',
    description: 'A powerful double-barreled shotgun. Devastating at close range, perfect for crowd control and launching enemies into the air.',
    image: '/images/weapons/shotgun.png',
    stats: { power: 80, speed: 40, range: 30 }
  },
  {
    id: 5,
    name: 'KALINA ANN II',
    type: 'Rocket Launcher',
    description: 'Lady\'s signature rocket launcher, upgraded version. Fires homing missiles and can be combined with the original for devastating dual-wielding attacks.',
    image: '/images/weapons/kalina-ann.png',
    stats: { power: 95, speed: 25, range: 85 }
  },
  {
    id: 6,
    name: 'BALROG',
    type: 'Devil Arm',
    description: 'Gauntlets and greaves infused with fire. Switch between Blow Mode for boxing and Kick Mode for martial arts. Ignites enemies with each strike.',
    image: '/images/weapons/balrog.png',
    stats: { power: 90, speed: 85, range: 40 }
  },
  {
    id: 7,
    name: 'LUCIFER',
    type: 'Devil Arm',
    description: 'A backpack-like weapon that generates unlimited spectral swords. Plant blades in enemies and detonate them for explosive damage.',
    image: '/images/weapons/lucifer.png',
    stats: { power: 75, speed: 70, range: 80 }
  },
  {
    id: 8,
    name: 'DR. FAUST',
    type: 'Devil Arm',
    description: 'A magical hat that uses Red Orbs as ammunition. High risk, high reward - spend orbs to deal massive damage or farm them from enemies.',
    image: '/images/weapons/faust.png',
    stats: { power: 100, speed: 60, range: 75 }
  }
];

function WeaponsSection() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const [selectedWeapon, setSelectedWeapon] = useState(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;

    gsap.fromTo(title,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          end: 'top 50%',
          scrub: 1
        }
      }
    );
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section ref={sectionRef} className="weapons-section" id="weapons">
      <div className="weapons-container">
        <div className="section-header">
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="visual-icon sword-icon"></span> ARSENAL
          </motion.span>
          <h2 ref={titleRef} className="section-title">WEAPONS</h2>
          <p className="section-subtitle">Dante's legendary collection of Devil Arms and Firearms</p>
        </div>

        <motion.div 
          className="weapons-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {weapons.map((weapon) => (
            <motion.div 
              key={weapon.id} 
              className="weapon-card"
              variants={cardVariants}
              whileHover={{ 
                scale: 1.03,
                boxShadow: '0 20px 60px rgba(196, 30, 58, 0.4)'
              }}
              onClick={() => setSelectedWeapon(weapon)}
              style={{ cursor: 'pointer' }}
            >
              <div className="weapon-image-container">
                <img src={weapon.image} alt={weapon.name} className="weapon-image" />
                <div className="weapon-glow"></div>
                <div className="weapon-overlay">
                  <span className="view-details">VIEW DETAILS</span>
                </div>
              </div>
              
              <div className="weapon-info">
                <span className="weapon-type">{weapon.type}</span>
                <h3 className="weapon-name">{weapon.name}</h3>
                <p className="weapon-description">{weapon.description}</p>
                
                <div className="weapon-stats">
                  <div className="stat-bar">
                    <span className="stat-label">POWER</span>
                    <div className="stat-track">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${weapon.stats.power}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.2 }}
                      />
                    </div>
                  </div>
                  <div className="stat-bar">
                    <span className="stat-label">SPEED</span>
                    <div className="stat-track">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${weapon.stats.speed}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                  <div className="stat-bar">
                    <span className="stat-label">RANGE</span>
                    <div className="stat-track">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${weapon.stats.range}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Weapon Modal */}
      <AnimatePresence>
        {selectedWeapon && (
          <motion.div 
            className="weapon-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedWeapon(null)}
          >
            <motion.div 
              className="weapon-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setSelectedWeapon(null)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>

              <div className="modal-content">
                <div className="modal-image-section">
                  <img src={selectedWeapon.image} alt={selectedWeapon.name} className="modal-weapon-image" />
                  <div className="modal-weapon-glow"></div>
                </div>

                <div className="modal-info-section">
                  <span className="modal-weapon-type">{selectedWeapon.type}</span>
                  <h2 className="modal-weapon-name">{selectedWeapon.name}</h2>
                  <p className="modal-weapon-description">{selectedWeapon.description}</p>

                  <div className="modal-stats">
                    <h3 className="stats-title">WEAPON STATS</h3>
                    <div className="modal-stat-bar">
                      <div className="stat-header">
                        <span className="stat-label">POWER</span>
                        <span className="stat-value">{selectedWeapon.stats.power}</span>
                      </div>
                      <div className="stat-track">
                        <motion.div 
                          className="stat-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${selectedWeapon.stats.power}%` }}
                          transition={{ duration: 0.8, delay: 0.2 }}
                        />
                      </div>
                    </div>
                    <div className="modal-stat-bar">
                      <div className="stat-header">
                        <span className="stat-label">SPEED</span>
                        <span className="stat-value">{selectedWeapon.stats.speed}</span>
                      </div>
                      <div className="stat-track">
                        <motion.div 
                          className="stat-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${selectedWeapon.stats.speed}%` }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                        />
                      </div>
                    </div>
                    <div className="modal-stat-bar">
                      <div className="stat-header">
                        <span className="stat-label">RANGE</span>
                        <span className="stat-value">{selectedWeapon.stats.range}</span>
                      </div>
                      <div className="stat-track">
                        <motion.div 
                          className="stat-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${selectedWeapon.stats.range}%` }}
                          transition={{ duration: 0.8, delay: 0.4 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default WeaponsSection;
